<template>
  <div id="summary" role="tabpanel" aria-labelledby="summary-tab">
    <div v-if="serverError" class="alert alert-danger" role="alert">
      {{ serverError }}
    </div>
    <div class="text-right expand-collapse">
      <b-link class="text-secondary" @click="expandAll">Expand All</b-link> |
      <b-link class="text-secondary" @click="collapseAll">Collapse All</b-link>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="card mb-2">
          <div class="card-header bg-primary text-white d-flex">
            <a v-b-toggle="`collapse-project-summary-card`" class="collapse-icon c-pointer">
              <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
                <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
            </a>
            <span class="font-weight-bold">Project Details</span>
            <span class="text-white ml-auto c-pointer">
              <span
                v-if="$store.getters['User/isCurrentUserAdmin']"
                @click="deleteConfirmationModal"
              >
                Delete |
              </span>
              <router-link
                v-if="!$store.getters['User/isCurrentUserViewOnly']"
                :to="{ name: 'edit project', params: { project_id: $route.params.project_id }}"
                class="text-white ml-auto c-pointer"
              >
                Edit
              </router-link>
            </span>

          </div>
          <b-collapse
            :id="`collapse-project-summary-card`"
            v-model="showSummaryCollapse"
          >
            <div class="card-body">
              <LoadingSpinner v-if="loading" message ='Loading Project Details' size = '1x'>
              </LoadingSpinner>
              <template v-if="!loading && project">
                <div class="form-group row">
                  <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6">
                    <DetailsRow label="Project Name" :field="project.name" />
                    <div id="localities-row" class="row">
                      <div class="col-4 label-text">Localities</div>
                      <div class="col-8">
                        <ul class="details-list">
                          <li v-for='locality in projectLocalities'
                            :key='locality.id'>
                            {{ locality }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <DetailsRow label="Total Acres" :field="getProjectTotalAcres(this.project)" />
                    <DetailsRow label="Partnership Grant?" :field="partnershipGrantExists()" />
                    <DetailsRow label="Project Status" :field="project.status" />
                    <DetailsRow label="Last Edited"
                      :field="project.lastEditedAt | dateString" />
                    <DetailsRow label="Last Edited By"
                      :field="project.lastEditedByName"
                    />
                    <DetailsRow
                      label="Project Description"
                      :field="project.description || 'None'"
                    />
                  </div>
                  <div class="col-6 d-none d-xs-none d-sm-none d-md-block">
                    <div class="row col">
                      <LoadingSpinner v-if="photoLoading"
                        message ='Loading Project Photo' size = '1x'>
                      </LoadingSpinner>
                      <div v-if="!photoLoading">
                        <div v-if="!project.photoBoxId">
                          <img id="placeholderPhoto" class="ml-auto"
                            src="../assets/VOFPlaceholder.png"
                            max-height="250" width="100%">
                        </div>
                        <div v-if="project.photoBoxId">
                          <img id="summaryPhoto"
                            v-if="project && project.photoBoxId && projectPhoto"
                            class="ml-auto"
                            :src = "projectPhoto"
                            max-height="250" width="100%">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </b-collapse>
        </div>
      </div>
    </div>
    <MapCard
      :baseUrl="mapUrl"
      :featureId="$route.params.project_id"
      title="Project"
      ref="mapCard"
    >
    </MapCard>
    <DevelopmentRightsSummary
      ref="developmentRightsSummary"
    />
    <OpenReviewsCard
      ref="reviewsCard"
    />
    <ProjectEventsCard
      ref="eventsCard"
    />
    <BoxCard
      v-if="project"
      storeNamespace="CurrentProject"
      :folderId="project.documentFolderId"
      :parentId="project.id"
      ref="boxCard"
    />
    <template v-if="!loading && project">
      <b-modal ref="deleteConfirmation" :id="`${project.id}`" :title="`Delete Project ${project.id}`">
        Are you sure you want to delete this Project? The associated Instrument and Property, as well as all associated entries, will be deleted.
        <div slot="modal-footer">
          <b-button variant="danger" class="button-row-spacing" @click="hideDeleteConfirmationModal">
            Cancel
          </b-button>
          <b-button @click="onDeleteConfirmed()">
            Delete
          </b-button>
        </div>
      </b-modal>
      <b-modal ref="deleteBlockerModal" :id="`blocker-${project.id}`" :title="`Cannot Delete Project ${project.id}`">
        {{ projectDeleteMessage }}
        <div slot="modal-footer">
          <b-button @click="hideDeleteBlockerModal">
            Okay
          </b-button>
        </div>
      </b-modal>
      <b-modal ref="deleteSuccessModal" :id="`deleteSuccess-${project.id}`" :title="`Successfully Deleted Project ${project.id}`">
        Folders in Box must be deleted manually. GIS polygon must be independently removed or edited.
        <div slot="modal-footer">
          <b-button @click="hideDeleteSuccessModal">
            Okay
          </b-button>
        </div>
      </b-modal>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import DetailsRow from '@/components/DetailsRow.vue';
import BoxCard from '../components/common/BoxCard.vue';
import DevelopmentRightsSummary from '@/components/project/DevelopmentRightsSummary.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import MapCard from '@/components/common/MapCard.vue';
import OpenReviewsCard from '@/components/reviews/OpenReviewsCard.vue';
import ProjectEventsCard from '@/components/event/ProjectEventsCard.vue';
import TotalAcresHelper from '../mixins/TotalAcresHelper';

export default {
  name: 'ProjectSummary',
  mixins: [TotalAcresHelper],
  components: {
    DetailsRow,
    DevelopmentRightsSummary,
    LoadingSpinner,
    MapCard,
    OpenReviewsCard,
    BoxCard,
    ProjectEventsCard,
  },
  data() {
    return {
      showSummaryCollapse: true,
      file: null,
      thumbnail: null,
      summaryFileCroppa: null,
    };
  },
  created() {
    this.$store.dispatch('fetchConfiguration');
    this.$store.dispatch('CurrentProject/fetchProjectDeleteMessage', { projectId: parseInt(this.$route.params.project_id, 10) });
  },
  mounted() {
    this.$store.dispatch('CurrentProject/fetchPhoto', { projectId: parseInt(this.$route.params.project_id, 10) });
  },
  computed: {
    ...mapState('CurrentProject', {
      project: state => state.currentProject,
      projectPhoto: state => state.projectPhoto,
      loading: state => state.loading,
      photoLoading: state => state.photoLoading,
      serverError: state => state.serverError,
      projectDeleteMessage: state => state.projectDeleteMessage,
    }),
    ...mapState({ mapUrl: state => state.Config.config.agolProjectTemplateUrl }),
    ...mapGetters('CurrentProject', [
      'projectLocalities',
    ]),
  },
  methods: {
    deleteConfirmationModal() {
      if (this.projectDeleteMessage !== '') {
        this.$refs.deleteBlockerModal.show();
        return;
      }
      this.$refs.deleteConfirmation.show();
    },
    onDeleteConfirmed() {
      this.$store.dispatch('CurrentProject/deleteProject', { projectId: this.project.id, submittedCallback: this.showDeleteSuccessModal });
    },
    hideDeleteConfirmationModal() {
      this.$refs.deleteConfirmation.hide();
    },
    hideDeleteBlockerModal() {
      this.$refs.deleteBlockerModal.hide();
    },
    hideDeleteSuccessModal() {
      // redirect to homepage after successfull delete
      this.$refs.deleteSuccessModal.hide();
      this.$router.push({
        name: 'home',
      });
    },
    showDeleteSuccessModal() {
      this.hideDeleteConfirmationModal();
      this.$refs.deleteSuccessModal.show();
    },
    getCardRefs() {
      return [this.$refs.mapCard,
        this.$refs.developmentRightsSummary,
        this.$refs.reviewsCard,
        this.$refs.eventsCard,
        this.$refs.boxCard,
      ];
    },
    expandAll() {
      this.showSummaryCollapse = true;
      this.getCardRefs().forEach((ref) => {
        ref.forceExpand();
      });
    },
    collapseAll() {
      this.showSummaryCollapse = false;
      this.getCardRefs().forEach((ref) => {
        ref.forceCollapse();
      });
    },
    partnershipGrantExists() {
      this.partnershipGrantsExist = false;
      // if (this.project.properties) {
      //   this.project.properties.forEach((property) => {
      //     if (property.propertyInstruments) {
      //       property.propertyInstruments.forEach((propertyInstrument) => {
      //         if (propertyInstrument.instrument.statusId === 2 // active
      //           && propertyInstrument.instrument.partnershipGrants
      //           && propertyInstrument.instrument.partnershipGrants.length > 0) {
      //           this.partnershipGrantsExist = true;
      //         }
      //       });
      //     }
      //   });
      // }
      return this.project.partnershipGrantsExist ? 'Yes' : 'No';
    },
  },
};
</script>
